import { useState } from "react";
import type {
  CycleResponse,
  LadderProjection,
  OperatorCloseRequest,
  PositionResponse,
} from "../../api/client";
import { OperatorCloseError, type OperatorCloseState } from "../../hooks/useOperatorClose";
import { cn } from "@/lib/utils";
import Panel from "@/components/common/Panel";
import ProtectionStepper from "@/components/common/ProtectionStepper";
import { ROLE_META, type PositionRole } from "@/lib/semantics";
import { ToneBadge } from "@/components/common/SemanticBadge";
import { legLatentEur, legRiskEur, isProfitLocked } from "@/lib/cockpit";

/** Plafond par côté §2 V6 (secours quand la projection est absente). */
const DEFAULT_MAX_SIDE = 3;

interface LadderPanelProps {
  cycle: CycleResponse | null;
  /** Positions du cycle (ouvertes + fermées). Null tant que non chargées. */
  positions: PositionResponse[] | null;
  /** Mid live (distance au trigger, latent, progression d'armement). Null sans tick. */
  mid?: number | null;
  /** Statut marché live — rougit l'alerte gap quand le marché n'est pas TRADEABLE. */
  marketStatus?: string | null;
  /** Plafond gelé par côté (V6 §2 = 3) — écrasé par la projection quand présente. */
  maxSide?: number;
  /**
   * « Fermer cette jambe » (2026-11-02) — fermeture opérateur d'UNE jambe, deux clics sans OTP. Absent
   * (historique, tests) ⇒ aucun bouton. Jamais sur la dernière jambe (« Arrêter » est le geste de fin).
   */
  operatorClose?: OperatorCloseState;
}

interface Trigger {
  kind: "reinforce" | "arm" | "cap" | "trail" | "arm-info";
  label: string;
  icon: string;
  /** Prix du seuil — null pour les lignes d'état (cap, arm-info). */
  price: number | null;
  cmp: "≤" | "≥" | null;
  /** Points restants avant déclenchement (null sans mid) ; ≤0 ⇒ atteint. */
  remaining: number | null;
  reached: boolean;
  /** Fraction 0..1 du chemin vers l'armement (kind arm seulement). */
  progress: number | null;
  /** Texte de droite pour les lignes d'état (cap / trail / arm-info). */
  note: string | null;
}

function num(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString("en-US", { maximumFractionDigits: digits });
}

function eur(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const s = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}€${s}`;
}

/** MTM brut live d'un ensemble de jambes (sans coût). Null sans mid. */
function sideLatentEur(ps: PositionResponse[], mid: number | null | undefined): number | null {
  if (mid == null) return null;
  return ps.reduce((acc, p) => acc + legLatentEur(p, mid), 0);
}

/** Fraction (0..1) vers le seuil d'armement : latent_net / (n·r_trail), miroir d'armTrails(). */
function armProgress(
  ps: PositionResponse[],
  mid: number | null | undefined,
  dir: "BUY" | "SELL",
  cost: number,
  rTrail: number,
): number | null {
  if (mid == null || ps.length === 0) return null;
  const sign = dir === "BUY" ? 1 : -1;
  const latent = ps.reduce((acc, p) => acc + (mid - Number(p.entryPrice)) * sign, 0) - ps.length * cost;
  const threshold = ps.length * rTrail;
  if (threshold <= 0) return null;
  return Math.max(0, Math.min(1, latent / threshold));
}

/** Plancher de P&L garanti une fois armé : Σ(trailStop − entrée)·dir·taille. */
function armedFloorEur(
  ps: PositionResponse[],
  dir: "BUY" | "SELL",
  extreme: number | null | undefined,
  rTrail: number | null | undefined,
): number | null {
  if (extreme == null || rTrail == null) return null;
  const trailStop = dir === "BUY" ? extreme - rTrail : extreme + rTrail;
  const sign = dir === "BUY" ? 1 : -1;
  return ps.reduce((acc, p) => acc + (trailStop - Number(p.entryPrice)) * sign * Number(p.sizePerPointEur), 0);
}

/**
 * Les lignes de pilotage du côté biais — la projection moteur rendue lisible :
 * renfort (si capacité), plafond (si plein — JAMAIS « renfort » à capacité pleine),
 * armement (dès trailArmCount, avec progression), et une fois armé : le trail
 * (stop suiveur, rt effectif, état de l'escalier).
 */
function nextTriggers(
  dir: "BUY" | "SELL",
  armed: boolean,
  ps: PositionResponse[],
  proj: LadderProjection | null,
  mid: number | null | undefined,
  extreme: number | null | undefined,
  armExtreme: number | null | undefined,
): Trigger[] {
  if (proj == null) return [];
  const cmp: "≤" | "≥" = dir === "BUY" ? "≥" : "≤";
  const out: Trigger[] = [];

  const build = (
    kind: Trigger["kind"],
    label: string,
    icon: string,
    price: number | null,
    progress: number | null,
    note: string | null = null,
  ): Trigger | null => {
    if (price == null && note == null) return null;
    let remaining: number | null = null;
    let reached = false;
    if (mid != null && price != null) {
      remaining = cmp === "≥" ? price - mid : mid - price;
      reached = remaining <= 0;
    }
    return { kind, label, icon, price, cmp: price != null ? cmp : null, remaining, reached, progress, note };
  };

  const count = ps.length;
  const armAt = proj.trailArmCount ?? proj.maxSide;
  const openAfterArm = proj.openAfterArm ?? true;
  const rt = proj.trailDistancePoints;
  const stairAdv = proj.trailStairAdvancePoints ?? 0;
  const stairRt = proj.trailStairTrailPoints ?? 0;
  const stairOn = stairAdv > 0 && stairRt > 0;

  const push = (t: Trigger | null) => {
    if (t != null) out.push(t);
  };

  if (armed) {
    // ── trail actif : stop suiveur = extrême ∓ rt effectif ; état de l'escalier ──
    const advance =
      extreme != null && armExtreme != null
        ? dir === "BUY"
          ? extreme - armExtreme
          : armExtreme - extreme
        : null;
    const stairActive = stairOn && advance != null && advance >= stairAdv;
    const rtEff = stairActive ? stairRt : rt;
    const trailStop = extreme != null ? (dir === "BUY" ? extreme - rtEff : extreme + rtEff) : null;
    const stairNote = !stairOn
      ? `rt ${rtEff} pts`
      : stairActive
        ? `rt ${rtEff} pts · escalier actif (${stairRt} après +${stairAdv})`
        : advance != null
          ? `rt ${rtEff} pts · escalier à +${Math.max(0, Math.ceil(stairAdv - advance))} pts`
          : `rt ${rtEff} pts · escalier ${stairRt}@+${stairAdv}`;
    push(build("trail", "trail — sortie", "⇄", trailStop, null, stairNote));
    if (count >= proj.maxSide) {
      push(build("cap", `plafond ${count}/${proj.maxSide} atteint`, "⛔", null, null, "plus de renfort"));
    } else if (openAfterArm) {
      push(build("reinforce", "renfort", "↑", proj.reinforceTriggerPrice, null));
    }
    return out;
  }

  if (count >= proj.maxSide) {
    push(build("cap", `plafond ${count}/${proj.maxSide} atteint`, "⛔", null, null, "plus de renfort"));
  } else {
    push(build("reinforce", "renfort", "↑", proj.reinforceTriggerPrice, null));
  }
  if (count >= armAt) {
    const armPrice = dir === "BUY" ? proj.buyArmPrice : proj.sellArmPrice;
    push(
      build(
        "arm",
        "le trail s'arme",
        "⤢",
        armPrice,
        armProgress(ps, mid, dir, proj.costPointsPerPosition, rt),
      ),
    );
  } else if (count > 0) {
    push(
      build("arm-info", "trail inactif", "⤢", null, null,
        `s'arme dès ${armAt} position${armAt > 1 ? "s" : ""} · rt ${rt} pts${stairOn ? ` (${stairRt} après +${stairAdv})` : ""}`),
    );
  }
  return out;
}

/**
 * Le ladder PROTECTED_LADDER_V6 — livre MONO-DIRECTIONNEL (défense retirée 2026-07-16) :
 * un seul côté (le biais) se construit jusqu'à {@code maxSide}, arme son trail
 * (count == n ET latent ≥ n·r_trail) puis cliquette son stop. Le panneau montre : le
 * prochain événement + son prix, la progression d'armement, les jambes (rôle,
 * entrée→stop, risque, âge, protection) et l'échelle de prix. Une carte « historique »
 * n'apparaît que si un vieux cycle mixte réhydraté porte encore des positions opposées.
 */
export default function LadderPanel({
  cycle,
  positions,
  mid,
  marketStatus,
  maxSide = DEFAULT_MAX_SIDE,
  operatorClose,
}: LadderPanelProps) {
  if (cycle == null) return null;
  const proj = cycle.ladderProjection ?? null;
  const effMaxSide = proj?.maxSide ?? maxSide;
  // Jambes fermées par l'opérateur : l'AUTO ne recomble pas (plafond auto = maxSide − n, définitif).
  const operatorClosed = cycle.operatorClosedCount ?? 0;
  const autoMaxSide = Math.max(0, effMaxSide - operatorClosed);
  const rTrail = proj?.trailDistancePoints ?? null;
  const open = (positions ?? []).filter((p) => p.closedAt == null);

  const bias = cycle.bias;
  const biasPs = open
    .filter((p) => p.direction === bias)
    .sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt));
  const oppositePs = open
    .filter((p) => p.direction !== bias)
    .sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt));

  const armed = bias === "BUY" ? cycle.buyTrailArmed === true : cycle.sellTrailArmed === true;
  const extreme = bias === "BUY" ? cycle.buyTrailExtreme : cycle.sellTrailExtreme;
  const armExtreme = bias === "BUY" ? cycle.buyArmExtreme : cycle.sellArmExtreme;
  // Pire-cas §7 par jambe (une jambe verrouillée en profit contribue 0).
  const worstCase = biasPs.reduce((acc, p) => acc + legRiskEur(p), 0);
  const latent = sideLatentEur(biasPs, mid);
  const triggers = nextTriggers(bias, armed, biasPs, proj, mid, extreme, armExtreme);
  const stop = biasPs[0] != null ? Number(biasPs[0].stopPrice) : null;
  const slDistance =
    mid == null || stop == null ? null : bias === "SELL" ? stop - mid : mid - stop;
  const floor = armed ? armedFloorEur(biasPs, bias, extreme ?? null, rTrail) : null;
  const gapExposure = biasPs.length === effMaxSide && !armed && worstCase > 0 ? worstCase : null;
  const marketOpen = marketStatus == null || marketStatus === "TRADEABLE";

  return (
    <Panel
      title="Ladder"
      aria-label="Ladder deux-trails"
      testId="ladder-panel"
      aside={
        <div className="flex items-center gap-2 tabular-nums">
          <span>
            swing lo <span className="text-foreground font-mono">{num(cycle.swingLo)}</span> · hi{" "}
            <span className="text-foreground font-mono">{num(cycle.swingHi)}</span>
          </span>
          <span data-testid="ladder-count">
            <span className="text-foreground font-mono">{biasPs.length}</span>/{effMaxSide}
          </span>
          {operatorClosed > 0 && (
            <span
              className="text-warning"
              data-testid="ladder-auto-cap"
              title={`${operatorClosed} jambe${operatorClosed > 1 ? "s" : ""} fermée${operatorClosed > 1 ? "s" : ""} par l'opérateur : l'AUTO ne recomble pas, son plafond est ${autoMaxSide} ; l'opérateur peut encore ajouter jusqu'à ${effMaxSide}.`}
            >
              auto ≤ {autoMaxSide}
            </span>
          )}
          {armed ? (
            <ToneBadge tone="buy" dot testId="ladder-status">
              TRAIL ARMÉ
            </ToneBadge>
          ) : biasPs.length > 0 ? (
            <ToneBadge tone="idle" dot testId="ladder-status">
              CONSTRUCTION
            </ToneBadge>
          ) : (
            <span className="text-muted-foreground" data-testid="ladder-status">
              vide
            </span>
          )}
        </div>
      }
      bodyClassName="flex flex-col gap-3"
    >
      {/* ── synthèse du côté biais ── */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {latent != null && (
          <span data-testid="ladder-latent">
            latent{" "}
            <span
              className={cn(
                "font-mono tabular-nums",
                latent > 0 ? "text-buy" : latent < 0 ? "text-sell" : "text-foreground",
              )}
            >
              {eur(latent)}
            </span>
          </span>
        )}
        <span data-testid="ladder-worstcase">
          pire-cas{" "}
          <span className="text-foreground font-mono tabular-nums">
            {worstCase > 0 ? `€${worstCase.toFixed(0)}` : "—"}
          </span>
        </span>
        {slDistance != null && (
          <span data-testid="ladder-sl">
            SL à <span className="text-foreground font-mono tabular-nums">{num(slDistance, 0)}</span> pts
          </span>
        )}
        {armed && floor != null && (
          <span data-testid="ladder-floor">
            plancher garanti{" "}
            <span className={cn("font-mono tabular-nums", floor >= 0 ? "text-buy" : "text-sell")}>
              {eur(floor)}
            </span>
          </span>
        )}
      </div>

      {/* ── alerte gap : plein & non armé ⇒ seul le SL catastrophe protège ── */}
      {gapExposure != null && (
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border border-dashed px-2 py-1 text-xs",
            marketOpen ? "border-warning/50 text-warning" : "border-sell/60 text-sell",
          )}
          data-testid="ladder-gap"
        >
          <span>⚠ SL catastrophe seul{marketOpen ? "" : " · marché fermé"}</span>
          <span className="font-mono tabular-nums">€{gapExposure.toFixed(0)} de risque de gap</span>
        </div>
      )}

      {/* ── prochains événements + prix (les lignes de pilotage) ── */}
      {triggers.map((t) => (
        <TriggerLine key={t.kind} bias={bias} trigger={t} />
      ))}

      {/* ── jambes (+ fermeture opérateur d'une jambe, 2026-11-02) ── */}
      {biasPs.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucune position ouverte.</p>
      ) : (
        <Legs biasPs={biasPs} mid={mid} operatorClose={operatorClose} />
      )}

      {/* ── échelle de prix : chaque niveau vs le mid live ── */}
      <LadderScale open={open} mid={mid} proj={proj} bias={bias} hasOpposite={oppositePs.length > 0} />

      {/* ── côté opposé : SEULEMENT si un vieux cycle mixte est réhydraté ── */}
      {oppositePs.length > 0 && (
        <div
          className="border-warning/40 flex flex-col gap-1.5 rounded-lg border border-dashed px-3 py-2"
          data-testid="ladder-opposite"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-warning text-xs font-semibold">
              Côté opposé — positions historiques (pré no-def)
            </span>
            <ToneBadge tone="warning">HISTORIQUE</ToneBadge>
          </div>
          <ul className="flex flex-col gap-1">
            {oppositePs.map((p) => (
              <Leg key={p.dealId} p={p} mid={mid} />
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

/** Une jambe : rôle · entrée→stop · taille · risque/verrou · âge · protection. */
/**
 * Liste des jambes + « Fermer cette jambe » (2026-11-02) : un bouton par jambe quand le cycle en a au
 * moins deux et qu'aucune demande n'est en cours ; deux clics (récapitulatif : entrée, latent, taille,
 * conséquence « l'AUTO ne recomble pas »), sans OTP ; issue (FILLED / REFUSED / EXPIRED) affichée dès le
 * tick suivant. État local isolé ici pour laisser LadderPanel sans hook (il rend null sans cycle).
 */
function Legs({
  biasPs,
  mid,
  operatorClose,
}: {
  biasPs: PositionResponse[];
  mid?: number | null;
  operatorClose?: OperatorCloseState;
}) {
  const [confirm, setConfirm] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const req = operatorClose?.status?.request ?? null;
  const inFlight = req?.status === "PENDING" || req?.status === "CONSUMED";
  const closable = operatorClose != null && biasPs.length > 1 && !inFlight && !operatorClose.pending;
  const target = confirm != null ? (biasPs.find((p) => p.dealId === confirm) ?? null) : null;
  const latent = target != null && mid != null ? legLatentEur(target, mid) : null;

  const doConfirm = async () => {
    if (operatorClose == null || target == null) return;
    setErr(null);
    setDismissed(null);
    try {
      await operatorClose.request(target.dealId);
    } catch (e) {
      setErr(e instanceof OperatorCloseError ? humanCloseRefusal(e.code, e.reason) : "Demande impossible — réessaie.");
    } finally {
      setConfirm(null);
    }
  };

  const showStatus = req != null && req.status !== "CANCELLED" && dismissed !== statusKey(req);

  return (
    <>
      <ul className="flex flex-col gap-1" data-testid="ladder-legs">
        {biasPs.map((p) => (
          <Leg key={p.dealId} p={p} mid={mid} onClose={closable ? () => setConfirm(p.dealId) : undefined} />
        ))}
      </ul>
      {target != null && (
        <div
          className="border-warning/45 bg-warning/5 flex flex-col gap-2 rounded-lg border p-2.5 text-xs"
          data-testid="ladder-close-confirm-box"
        >
          <p className="text-foreground font-medium">Fermer cette jambe au marché ?</p>
          <p className="text-muted-foreground tabular-nums">
            {ROLE_META[target.role as PositionRole]?.label ?? target.role} ·{" "}
            <span className="font-mono">{Number(target.entryPrice).toFixed(1)}</span> ·{" "}
            <span className="font-mono">{Number(target.sizePerPointEur).toFixed(1)} €/pt</span>
            {latent != null && (
              <>
                {" "}
                · latent{" "}
                <span
                  className={cn("font-mono", latent > 0 ? "text-buy" : latent < 0 ? "text-sell" : "text-foreground")}
                  data-testid="ladder-close-latent"
                >
                  {eur(latent)}
                </span>
              </>
            )}
          </p>
          <p className="text-muted-foreground">
            Exécutée au tick suivant, un seul essai. L'AUTO ne recomblera pas cette place (son plafond baisse
            de 1 pour tout le cycle) ; toi, tu peux encore en ajouter.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={doConfirm}
              className="bg-warning text-background rounded-lg px-3 py-1 font-semibold"
              data-testid="ladder-close-confirm"
            >
              Oui, fermer cette jambe
            </button>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-3 py-1"
              data-testid="ladder-close-cancel"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {err && (
        <p className="text-loss text-xs" role="alert" data-testid="ladder-close-error">
          {err}
        </p>
      )}
      {showStatus && req != null && (
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-xs",
            inFlight
              ? "border-border text-muted-foreground"
              : req.status === "FILLED"
                ? "border-buy/40 text-buy"
                : "border-sell/50 text-sell",
          )}
          data-testid="ladder-close-status"
          data-status={req.status}
        >
          <span>{closeStatusText(req)}</span>
          {!inFlight && (
            <button
              type="button"
              onClick={() => setDismissed(statusKey(req))}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Masquer"
              data-testid="ladder-close-dismiss"
            >
              ×
            </button>
          )}
        </div>
      )}
    </>
  );
}

function statusKey(req: OperatorCloseRequest): string {
  return `${req.dealId}:${req.requestedAt}:${req.status}`;
}

function closeStatusText(req: OperatorCloseRequest): string {
  switch (req.status) {
    case "PENDING":
    case "CONSUMED":
      return `Fermeture de ${req.dealId} en cours… (au tick suivant)`;
    case "FILLED":
      return `Jambe ${req.dealId} fermée par l'opérateur — l'AUTO ne recomble pas cette place.`;
    case "EXPIRED":
      return `Fermeture de ${req.dealId} expirée : aucun tick en 10 s (marché fermé ou flux arrêté).`;
    default:
      return `Fermeture de ${req.dealId} refusée : ${humanCloseReason(req.reason ?? "")}`;
  }
}

/** Motif de refus serveur (409) → phrase. */
function humanCloseRefusal(code: string, reason: string): string {
  switch (code) {
    case "LAST_LEG":
      return "Dernière jambe du cycle : utilise « Arrêter ».";
    case "NOT_OPEN":
      return "Cette jambe n'est plus ouverte.";
    case "REQUEST_PENDING":
      return "Une fermeture est déjà en cours.";
    case "STREAM_NOT_STARTED":
      return "Flux arrêté : aucun tick pour exécuter la fermeture.";
    case "NO_ACTIVE_CYCLE":
      return "Aucun cycle actif.";
    default:
      return reason || code;
  }
}

/** Motif de refus au tick (« CODE: détail ») → phrase. */
function humanCloseReason(reason: string): string {
  const code = reason.split(":")[0] ?? "";
  switch (code) {
    case "LAST_LEG":
      return "dernière jambe du cycle, utilise « Arrêter ».";
    case "NOT_OPEN":
      return "la jambe n'était plus ouverte au tick.";
    case "ALREADY_CLOSING":
      return "la mécanique fermait déjà ce côté à ce tick.";
    case "CLOSE_REJECTED":
      return `IG a rejeté la fermeture (${reason.slice(code.length + 1).trim()}).`;
    case "CYCLE_CHANGED":
      return "le cycle a changé entre le clic et le tick.";
    default:
      return reason || "sans motif.";
  }
}

function Leg({ p, mid, onClose }: { p: PositionResponse; mid?: number | null; onClose?: () => void }) {
  return (
    <li className="border-border/50 flex items-center justify-between gap-2 border-t pt-1 text-xs first:border-t-0 first:pt-0">
      <span
        className={cn("text-muted-foreground w-14 shrink-0", p.role === "OPERATEUR" && "text-warning font-medium")}
        data-testid={`ladder-leg-role-${p.role}`}
      >
        {ROLE_META[p.role as PositionRole]?.label ?? p.role}
      </span>
      <span className="font-mono tabular-nums">
        {Number(p.entryPrice).toFixed(0)} → {Number(p.stopPrice).toFixed(0)}
      </span>
      <span className="text-muted-foreground shrink-0 font-mono tabular-nums">
        {Number(p.sizePerPointEur).toFixed(1)} €/pt
      </span>
      <LegLatent p={p} mid={mid} />
      <LegRisk p={p} />
      <span className="text-muted-foreground shrink-0 tabular-nums">{legAge(p.openedAt, mid)}</span>
      <ProtectionStepper state={p.protectedState} />
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="border-border text-muted-foreground hover:border-warning hover:text-warning shrink-0 rounded-md border px-1.5 py-0.5"
          title="Fermer cette jambe au marché (deux clics, sans OTP)"
          aria-label={`Fermer la jambe ${p.dealId}`}
          data-testid={`ladder-leg-close-${p.dealId}`}
        >
          Fermer
        </button>
      )}
    </li>
  );
}

/** Gain/perte latent de la jambe au mid : (mid − entrée)·dir·taille, signé. « — » sans tick. */
function LegLatent({ p, mid }: { p: PositionResponse; mid?: number | null }) {
  const latent = mid != null ? legLatentEur(p, mid) : null;
  return (
    <span
      className={cn(
        "shrink-0 font-mono tabular-nums",
        latent == null ? "text-muted-foreground" : latent > 0 ? "text-buy" : latent < 0 ? "text-sell" : "text-foreground",
      )}
      title="Gain/perte latent de cette jambe au mid (hors coût de portage)."
      data-testid={`ladder-leg-latent-${p.dealId}`}
    >
      {eur(latent)}
    </span>
  );
}

/** Contribution §7 de la jambe : "€X" rouge à risque, "verrouillé" vert quand le stop est en profit. */
function LegRisk({ p }: { p: PositionResponse }) {
  if (isProfitLocked(p)) {
    return (
      <span
        className="text-buy shrink-0"
        title="Verrouillé : cette jambe ne peut plus perdre (contribue 0 au pire-cas)."
      >
        verrouillé
      </span>
    );
  }
  return (
    <span
      className="text-sell shrink-0 font-mono tabular-nums"
      title="€ à risque si le stop de cette jambe est touché."
    >
      €{legRiskEur(p).toFixed(0)}
    </span>
  );
}

/** Âge compact de la jambe ; {@code mid} inutilisé mais garde la cadence de re-render du tick. */
function legAge(openedAtIso: string, _mid?: number | null): string {
  const start = Date.parse(openedAtIso);
  if (Number.isNaN(start)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  const hours = Math.floor(secs / 3600);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}j${hours % 24}h`;
}

/** « Quoi ensuite, à quel prix, à quelle distance » — la projection rendue lisible. */
function TriggerLine({ bias, trigger }: { bias: "BUY" | "SELL"; trigger: Trigger }) {
  const { kind, icon, label, price, cmp, remaining, reached, progress, note } = trigger;
  const accent = bias === "BUY" ? "text-buy" : "text-sell";
  const muted = kind === "cap" || kind === "arm-info";
  return (
    <div
      className={cn(
        "border-border/60 bg-surface-2 flex flex-col gap-1 rounded-md border border-dashed px-2 py-1.5 text-xs",
        muted && "opacity-80",
      )}
      data-testid="ladder-trigger"
      data-kind={trigger.kind}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">
          <span className={cn("mr-1", muted ? "text-muted-foreground" : accent)}>{icon}</span>
          {label}
          {price != null && (
            <>
              {" "}
              <span className="font-mono">{cmp}</span>{" "}
              <span className="text-foreground font-mono tabular-nums">{num(price)}</span>
            </>
          )}
        </span>
        <span className="text-muted-foreground tabular-nums">
          {note != null ? (
            note
          ) : remaining == null ? (
            "—"
          ) : reached ? (
            <span className={accent}>atteint</span>
          ) : (
            <>
              <span className="text-foreground font-mono">{num(remaining, 0)}</span> pts
            </>
          )}
        </span>
      </div>
      {progress != null && (
        <div className="bg-border/60 h-1 overflow-hidden rounded-full" aria-hidden>
          <div
            className={cn("h-full rounded-full", bias === "BUY" ? "bg-buy" : "bg-sell")}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

interface LadderMarker {
  price: number;
  tag: string;
  tone: string;
  strong?: boolean;
}

/**
 * Échelle de prix verticale — chaque niveau actionnable (stops, prix d'armement, trigger
 * de renfort) et le mid live, prix hauts en haut. Les niveaux du côté opposé ne sont
 * tracés que si des positions historiques y vivent encore.
 */
function LadderScale({
  open,
  mid,
  proj,
  bias,
  hasOpposite,
}: {
  open: PositionResponse[];
  mid: number | null | undefined;
  proj: LadderProjection | null;
  bias: "BUY" | "SELL";
  hasOpposite: boolean;
}) {
  const markers: LadderMarker[] = [];
  const push = (price: number | null | undefined, tag: string, tone: string, strong = false) => {
    if (price != null && Number.isFinite(Number(price)))
      markers.push({ price: Number(price), tag, tone, strong });
  };

  const biasStop = open.find((p) => p.direction === bias)?.stopPrice;
  push(biasStop, `stop ${bias}`, bias === "BUY" ? "buy" : "sell");
  push(
    bias === "BUY" ? proj?.buyArmPrice : proj?.sellArmPrice,
    "armement",
    bias === "BUY" ? "buy-dim" : "sell-dim",
  );
  push(proj?.reinforceTriggerPrice, "renfort", "accent");
  if (hasOpposite) {
    const oppStop = open.find((p) => p.direction !== bias)?.stopPrice;
    push(oppStop, `stop ${bias === "BUY" ? "SELL" : "BUY"} (hist.)`, bias === "BUY" ? "sell" : "buy");
  }
  if (mid != null) push(mid, "mid", "mid", true);

  if (markers.length < 2) return null;

  const prices = markers.map((m) => m.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;
  const pad = span * 0.08;
  const lo = min - pad;
  const hi = max + pad;
  const pos = (p: number) => ((hi - p) / (hi - lo)) * 100;

  const toneClass: Record<string, string> = {
    sell: "text-sell",
    "sell-dim": "text-sell/70",
    buy: "text-buy",
    "buy-dim": "text-buy/70",
    accent: "text-accent",
    mid: "text-foreground",
  };
  const lineClass: Record<string, string> = {
    sell: "bg-sell",
    "sell-dim": "bg-sell/50",
    buy: "bg-buy",
    "buy-dim": "bg-buy/50",
    accent: "bg-accent/70",
    mid: "bg-foreground",
  };

  return (
    <div
      className="border-border bg-surface-2 relative rounded-lg border px-3 py-2"
      style={{ height: 168 }}
      data-testid="ladder-scale"
      aria-label="Échelle de prix"
    >
      {markers.map((m) => (
        <div
          key={`${m.tag}-${m.price}`}
          className="absolute right-3 left-3 flex items-center gap-2"
          style={{ top: `${pos(m.price)}%`, transform: "translateY(-50%)" }}
        >
          <span
            className={cn(
              "w-24 shrink-0 text-right text-[0.62rem] tracking-wide uppercase",
              toneClass[m.tone] ?? "text-muted-foreground",
              m.strong && "font-bold",
            )}
          >
            {m.tag}
          </span>
          <span className={cn("h-px flex-1", lineClass[m.tone] ?? "bg-border", m.strong && "h-0.5")} />
          <span
            className={cn(
              "shrink-0 font-mono text-[0.68rem] tabular-nums",
              toneClass[m.tone] ?? "text-muted-foreground",
              m.strong && "font-bold",
            )}
          >
            {num(m.price)}
          </span>
        </div>
      ))}
    </div>
  );
}
